"use client"

import { useEffect, useCallback, useRef } from "react"

type KeyHandler = (e: KeyboardEvent) => void

interface ShortcutConfig {
  key: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  meta?: boolean
  handler: KeyHandler
  preventDefault?: boolean
}

export function useKeyboardShortcuts(
  shortcuts: ShortcutConfig[],
  enabled: boolean = true
) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return

      const target = e.target as HTMLElement
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return
      }

      for (const shortcut of shortcuts) {
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase()
        const ctrlMatch = shortcut.ctrl ? e.ctrlKey || e.metaKey : !e.ctrlKey && !e.metaKey
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey
        const altMatch = shortcut.alt ? e.altKey : !e.altKey

        if (keyMatch && ctrlMatch && shiftMatch && altMatch) {
          if (shortcut.preventDefault !== false) {
            e.preventDefault()
          }
          shortcut.handler(e)
          return
        }
      }
    },
    [shortcuts, enabled]
  )

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])
}

export function useUndoRedo<T>(initialState: T, maxHistory: number = 50) {
  const historyRef = useRef<T[]>([initialState])
  const indexRef = useRef(0)

  const pushState = useCallback(
    (state: T) => {
      const newHistory = historyRef.current.slice(0, indexRef.current + 1)
      newHistory.push(state)
      if (newHistory.length > maxHistory) {
        newHistory.shift()
      } else {
        indexRef.current++
      }
      historyRef.current = newHistory
    },
    [maxHistory]
  )

  const undo = useCallback((): T | null => {
    if (indexRef.current > 0) {
      indexRef.current--
      return historyRef.current[indexRef.current]
    }
    return null
  }, [])

  const redo = useCallback((): T | null => {
    if (indexRef.current < historyRef.current.length - 1) {
      indexRef.current++
      return historyRef.current[indexRef.current]
    }
    return null
  }, [])

  const canUndo = useCallback(() => indexRef.current > 0, [])
  const canRedo = useCallback(() => indexRef.current < historyRef.current.length - 1, [])

  const reset = useCallback((state: T) => {
    historyRef.current = [state]
    indexRef.current = 0
  }, [])

  return { pushState, undo, redo, canUndo, canRedo, reset }
}
